import { useState } from "react";
import { uploadEpeg, runGnff, downloadGnff, runBip } from "../api/client";
import JobLog from "../components/JobLog";

function triggerBrowserDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const TABS = ["EPEG", "GNFF", "BIP"];

function EpegForm() {
  const [files, setFiles]                   = useState([]);
  const [data, setData]                     = useState("");
  const [comissao, setComissao]             = useState("");
  const [cotacao, setCotacao]               = useState("");
  const [frete, setFrete]                   = useState("");
  const [freteManual, setFreteManual]       = useState(false);
  // Filial de destino: "" = automático (XML lê o destinatário; PDF/imagem = matriz)
  const [filial, setFilial]                 = useState("");
  const [jobId, setJobId]                   = useState(null);
  const [running, setRunning]               = useState(false);
  const [error, setError]                   = useState("");

  const inferMode = (filename = "") => {
    const ext = filename.split(".").pop().toLowerCase();
    if (ext === "pdf") return "pdf";
    if (ext === "xml") return "xml";
    return "imagem";
  };

  const mode = inferMode(files[0]?.name);

  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    if (files.length === 0) { setError("Selecione ao menos 1 arquivo."); return; }
    if (mode !== "xml" && !cotacao) { setError("Informe a cotação do dólar."); return; }
    if (freteManual) { setError("Distribuição manual ainda não disponível no frontend — use o CLI diretamente."); return; }
    if (!frete) { setError("Informe o frete total da nota."); return; }
    setError("");
    setRunning(true);
    setJobId(null);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      fd.append("mode", mode);
      if (data) fd.append("data_aquisicao", data);
      if (comissao && mode !== "xml") fd.append("comissao", comissao);
      if (cotacao && mode !== "xml") fd.append("cotacao_dolar", cotacao);
      if (frete) fd.append("frete_total", frete);
      if (filial) fd.append("filial", filial);
      const { data: res } = await uploadEpeg(fd);
      setJobId(res.job_id);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      setRunning(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Nota(s) fiscal(is)
        </label>
        <input
          type="file"
          multiple
          accept=".jpg,.jpeg,.png,.pdf,.xml"
          onChange={(e) => setFiles(Array.from(e.target.files || []))}
          className="block w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-800 hover:file:bg-blue-100 cursor-pointer"
          required
        />
        <p className="mt-1 text-xs text-gray-500">
          Selecione 1 arquivo, ou vários de uma vez (ctrl/cmd+clique) quando a mesma compra veio dividida em várias notas — o frete é rateado pela quantidade combinada de todas. Todos devem ser do mesmo tipo (imagem, PDF ou XML).
        </p>
        {files.length > 0 && (
          <div className="mt-2 space-y-1">
            <p className="text-xs text-gray-500">
              Modo detectado: <span className="font-semibold">{mode.toUpperCase()}</span> — {files.length} arquivo{files.length > 1 ? "s" : ""}
            </p>
            <ul className="text-xs text-gray-600 space-y-0.5">
              {files.map((f, idx) => (
                <li key={`${f.name}-${idx}`} className="flex items-center gap-2">
                  <span className="truncate">{f.name}</span>
                  <button
                    type="button"
                    onClick={() => removeFile(idx)}
                    className="text-red-500 hover:underline shrink-0"
                  >
                    remover
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Filial de destino</label>
        <select
          value={filial}
          onChange={(e) => setFilial(e.target.value)}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">{mode === "xml" ? "Automático — destinatário do XML" : "Matriz (Caxias do Sul) — SKU EG-"}</option>
          <option value="matriz">Matriz (Caxias do Sul) — SKU EG-</option>
          <option value="sp">Filial São Paulo — SKU EGSP-</option>
        </select>
        <p className="mt-1 text-xs text-gray-400">
          {mode === "xml"
            ? "No XML a filial é lida do destinatário (UF SP = filial SP). Se escolher uma filial que não bate com o XML, a importação para com erro."
            : "Em PDF/imagem o destinatário não é lido: escolha Filial São Paulo quando a nota foi faturada para SP."}
        </p>
      </div>

      <div className="flex gap-4">
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-1">Data de Aquisição</label>
          <input
            type="date"
            value={data}
            onChange={(e) => setData(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        {mode !== "xml" && (
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Comissão (%)</label>
            <input
              type="number"
              value={comissao}
              onChange={(e) => setComissao(e.target.value)}
              placeholder="ex: 25"
              min="0"
              max="100"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        )}
      </div>

      <div className="flex gap-4">
        {mode !== "xml" && (
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Cotação do Dólar (R$)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={cotacao}
              onChange={(e) => setCotacao(e.target.value)}
              placeholder="ex: 5.80"
              required
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        )}
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Frete Total da Nota (R$)
          </label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={frete}
            onChange={(e) => setFrete(e.target.value)}
            placeholder="ex: 45.00"
            disabled={freteManual}
            required={!freteManual}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
          />
        </div>
      </div>

      <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
        <input
          type="checkbox"
          checked={freteManual}
          onChange={(e) => { setFreteManual(e.target.checked); setFrete(""); }}
          className="rounded"
        />
        <span className="text-gray-700">Distribuição manual por item</span>
        <span className="text-gray-400 text-xs">(ex: duas remessas ou produtos maiores)</span>
      </label>
      {freteManual ? (
        <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          Distribuição manual ainda não disponível no frontend. Use o CLI diretamente:<br />
          <code className="font-mono">python -m epeg.main --mode … --input … --output …</code>
        </p>
      ) : frete ? (
        <p className="text-xs text-gray-500">
          O frete será distribuído proporcionalmente pela quantidade de cada item.
        </p>
      ) : null}

      {error && <p className="text-red-500 text-sm">{error}</p>}
      <button
        type="submit"
        disabled={running && !jobId}
        className="bg-blue-800 hover:bg-blue-700 disabled:bg-gray-300 text-white font-semibold px-6 py-2 rounded-lg text-sm"
      >
        Enviar e Processar
      </button>
      <JobLog jobId={jobId} onDone={() => setRunning(false)} />
    </form>
  );
}

function GnffForm() {
  const [jobId, setJobId]         = useState(null);
  const [running, setRunning]     = useState(false);
  const [error, setError]         = useState("");
  const [downloaded, setDownloaded] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setRunning(true);
    setDownloaded(false);
    setJobId(null);
    try {
      const { data } = await runGnff();
      setJobId(data.job_id);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      setRunning(false);
    }
  };

  const onDone = async (status) => {
    setRunning(false);
    if (status !== "done") return;
    try {
      const { data: blob } = await downloadGnff(jobId);
      triggerBrowserDownload(blob, "gnff_nota_entrada.xml");
      setDownloaded(true);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <p className="text-sm text-gray-500">
        Gera o XML de nota fiscal de entrada com base nos SKUs da planilha e baixa automaticamente
        no navegador — não fica arquivo salvo no servidor. Não requer parâmetros.
      </p>
      {error && <p className="text-red-500 text-sm">{error}</p>}
      <button
        type="submit"
        disabled={running && !jobId}
        className="bg-blue-800 hover:bg-blue-700 disabled:bg-gray-300 text-white font-semibold px-6 py-2 rounded-lg text-sm"
      >
        Executar
      </button>
      {downloaded && <p className="text-green-600 text-sm">✓ Arquivo baixado.</p>}
      <JobLog jobId={jobId} onDone={onDone} />
    </form>
  );
}

function BipForm() {
  const [mode, setMode]       = useState("sku");
  const [sku, setSku]         = useState("");
  const [skuFile, setSkuFile] = useState("");
  const [downloadAll, setDownloadAll] = useState(false);
  const [force, setForce]     = useState(false);
  const [limit, setLimit]     = useState("");
  const [jobId, setJobId]     = useState(null);
  const [running, setRunning] = useState(false);
  const [error, setError]     = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setRunning(true);
    setJobId(null);
    try {
      const body = { mode, download_all: downloadAll, force };
      if (mode === "sku") body.sku = sku.trim();
      if (mode === "sku-file") body.sku_file = skuFile.trim();
      if (limit) body.limit = parseInt(limit, 10);
      const { data } = await runBip(body);
      setJobId(data.job_id);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      setRunning(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Modo</label>
        <div className="flex gap-4 flex-wrap">
          {[
            { val: "sku",           label: "SKU único" },
            { val: "sku-file",      label: "Arquivo de SKUs" },
            { val: "missing-bling", label: "Faltantes no Bling" },
          ].map(({ val, label }) => (
            <label key={val} className="flex items-center gap-1.5 text-sm cursor-pointer">
              <input type="radio" name="bip-mode" value={val} checked={mode === val} onChange={() => setMode(val)} />
              {label}
            </label>
          ))}
        </div>
      </div>

      {mode === "sku" && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">SKU</label>
          <input
            type="text"
            value={sku}
            onChange={(e) => setSku(e.target.value)}
            placeholder="EG-FK-XXXX"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>
      )}

      {mode === "sku-file" && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Caminho do arquivo</label>
          <input
            type="text"
            value={skuFile}
            onChange={(e) => setSkuFile(e.target.value)}
            placeholder="data/entrada/skus.txt"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>
      )}

      <div className="flex gap-4 flex-wrap">
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={downloadAll} onChange={(e) => setDownloadAll(e.target.checked)} className="rounded" />
          Baixar todas as imagens
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} className="rounded" />
          Forçar re-download
        </label>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Limite (opcional)</label>
        <input
          type="number"
          value={limit}
          onChange={(e) => setLimit(e.target.value)}
          placeholder="ex: 50"
          min="1"
          className="w-32 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {error && <p className="text-red-500 text-sm">{error}</p>}
      <button
        type="submit"
        disabled={running && !jobId}
        className="bg-blue-800 hover:bg-blue-700 disabled:bg-gray-300 text-white font-semibold px-6 py-2 rounded-lg text-sm"
      >
        Executar
      </button>
      <JobLog jobId={jobId} onDone={() => setRunning(false)} />
    </form>
  );
}

const FORMS = { EPEG: EpegForm, GNFF: GnffForm, BIP: BipForm };

export default function Modulos() {
  const [tab, setTab] = useState("EPEG");
  const Form = FORMS[tab];

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Módulos</h2>

      <div className="bg-white rounded-xl shadow">
        {/* Tabs */}
        <div className="flex border-b">
          {TABS.map((t) => (
            <button
              type="button"
              key={t}
              onClick={() => setTab(t)}
              className={`px-6 py-3 text-sm font-medium transition-colors ${
                tab === t
                  ? "border-b-2 border-blue-800 text-blue-800"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Form */}
        <div className="p-6">
          <Form />
        </div>
      </div>
    </div>
  );
}
